import { addDays, daysInMonth, monthKey, toDate, todayIso, weekday } from './FieldOpsKit'
import { buildDriversSeed, buildFieldSeed, buildSalesSeed, plannedDates } from './demoData'
import { miles } from './geo'
import { buildUnrouted } from './unrouted'

/**
 * Field Ops demo store (2026-10-29): a plain reducer over in-memory sample data, plus the selectors the screens
 * share. Pure functions only — the real backend will replace `dispatch` with endpoint calls and these selectors
 * with queries, keeping the same shapes.
 */

export const initialState = () => {
    const sales = buildSalesSeed()
    const drivers = buildDriversSeed(sales.stores)
    return { sales: sales, drivers: drivers, field: buildFieldSeed(sales, drivers) }
}

// The demo session is saved as a log of actions and replayed on the next page load (demoSession.js). Every logged
// action carries its own timestamp (`ts`) and the reducer uses it as "now", so a replay gives the same times.
let clock = null
const nowStamp = () => (clock === null ? Date.now() : clock)
const hhmm = () => { const d = new Date(nowStamp()); return d.getHours() + ':' + String(d.getMinutes()).padStart(2, '0') }
const startHour = (w) => {
    const nums = w.match(/\d+/g) || ['0', '0']
    const s = Number(nums[0]); const e = Number(nums[1])
    return w.indexOf('PM') >= 0 && e !== 12 && s <= e && s !== 12 ? s + 12 : s
}

/* ======================================================================================================
 * SELECTORS
 * ==================================================================================================== */

export const paceFraction = (month) => {
    const today = todayIso(); const cur = monthKey(today)
    if (month < cur) return 1
    if (month > cur) return 0
    return toDate(today).getDate() / daysInMonth(cur)
}

/** Goal of a store for a month. `official` = the published snapshot (what salespeople see). */
export const goalOf = (sales, month, storeId, official) => {
    const p = sales.periods[month]
    const src = official ? (p && p.published ? p.published : (p && p.status === 'published' ? sales.goals[month] : null)) : sales.goals[month]
    if (!src) return null
    const v = src[storeId]
    return v === undefined || v === null ? null : v
}

export const salesOf = (sales, month, storeId) =>
    sales.orders.reduce((a, o) => (o.storeId === storeId && monthKey(o.date) === month ? a + o.total : a), 0)

export const visitsIn = (sales, month, filter) => sales.visits.filter((v) => monthKey(v.date) === month && (!filter || filter(v)))

/** Visit counts for a set of visits: due = already past or done; compliance = done / (done + missed). */
export const visitStats = (visits) => {
    const today = todayIso()
    let done = 0, missed = 0, upcoming = 0, resched = 0
    visits.forEach((v) => {
        if (v.status === 'done') done++
        else if (v.status === 'missed') missed++
        else if (v.status === 'rescheduled') resched++
        else if (v.status === 'in_progress') upcoming++
        else if (v.date >= today) upcoming++
        else missed++
    })
    return { done: done, missed: missed, planned: done + missed + upcoming, upcoming: upcoming, rescheduled: resched, total: visits.length - resched }
}

export const storeState = (goal, sales, month) => {
    if (goal === null || goal === undefined) return { key: 'nogoal', label: 'No goal', tone: 'gray' }
    if (goal > 0 && sales >= goal) return { key: 'met', label: 'Goal met', tone: 'green' }
    const pace = paceFraction(month)
    if (month < monthKey(todayIso())) return { key: 'behind', label: 'Missed', tone: 'red' }
    if (pace === 0) return { key: 'ok', label: 'Not started', tone: 'gray' }
    if (sales >= goal * pace * 0.9) return { key: 'ok', label: 'On track', tone: 'blue' }
    return { key: 'behind', label: 'Behind', tone: 'amber' }
}

export const routesOf = (sales) => {
    const names = []
    sales.stores.forEach((s) => { if (names.indexOf(s.route) < 0) names.push(s.route) })
    return names
}

/** Agenda of one salesperson on one date: their visits sorted by the store's order in the day. */
export const agendaFor = (sales, rep, date) => {
    const byId = {}
    sales.stores.forEach((s) => { byId[s.id] = s })
    return sales.visits
        .filter((v) => v.rep === rep && v.date === date && v.status !== 'rescheduled')
        .map((v) => ({ visit: v, store: byId[v.storeId] }))
        .sort((a, b) => (ordOf(a) - ordOf(b)) || a.store.name.localeCompare(b.store.name))
}
const ordOf = (x) => (x.visit.ord !== undefined && x.visit.ord !== null ? x.visit.ord : x.store.seq)

export const orderedStops = (drivers, date, driverId) => {
    return drivers.deliveries
        .filter((d) => d.date === date && (driverId === undefined ? true : d.driverId === driverId))
        .sort((a, b) => {
            const ka = a.stopOrder ? a.stopOrder : 1000 + startHour(a.window) * 10 + a.seq / 1000
            const kb = b.stopOrder ? b.stopOrder : 1000 + startHour(b.window) * 10 + b.seq / 1000
            return ka - kb
        })
}

export const isDelayed = (d) => d.date < todayIso() && (d.status === 'planned' || d.status === 'out')
export const hasFailedAttempt = (d) => d.events.some((e) => e.type === 'failed')

/* ======================================================================================================
 * FIELD APP SELECTORS (2026-11-04)
 * ==================================================================================================== */

/** Who is acting in the field app: a salesperson or a driver (null for administrators). */
export const actorOf = (me) => {
    if (me.kind === 'rep') return { kind: 'rep', key: me.id, name: me.name, rep: me.rep }
    if (me.kind === 'driver') return { kind: 'driver', key: me.id, name: me.name, driverId: me.driverId }
    return null
}

/** "Today" for the demo: on a weekend the next Monday is used, because the agenda only has Monday–Friday. */
export const workDate = () => {
    const t = todayIso()
    const w = weekday(t)
    return w === 0 ? addDays(t, 1) : w === 6 ? addDays(t, 2) : t
}
export const isRealToday = (date) => date === todayIso()

export const dayKey = (actorKey, date) => actorKey + '|' + date
export const dayOf = (field, actorKey, date) => field.days[dayKey(actorKey, date)] || null

export const storeMap = (sales) => {
    const m = {}
    sales.stores.forEach((s) => { m[s.id] = s })
    return m
}

/** Visits grouped by store (build once per render, then pass to storeInfo / nearbyCandidates). */
export const visitIndex = (sales) => {
    const m = {}
    sales.visits.forEach((v) => { (m[v.storeId] = m[v.storeId] || []).push(v) })
    return m
}
export const orderIndex = (sales) => {
    const m = {}
    sales.orders.forEach((o) => { (m[o.storeId] = m[o.storeId] || []).push(o) })
    return m
}

/** Everything the salesperson wants to know about a store before and during a visit. */
export const storeInfo = (sales, storeId, vIdx, oIdx) => {
    const today = todayIso()
    const month = monthKey(today)
    const goal = goalOf(sales, month, storeId, true)
    const sold = salesOf(sales, month, storeId)
    const visits = (vIdx ? vIdx[storeId] : sales.visits.filter((v) => v.storeId === storeId)) || []
    const orders = ((oIdx ? oIdx[storeId] : sales.orders.filter((o) => o.storeId === storeId)) || []).slice().sort((a, b) => (a.date < b.date ? 1 : -1))
    const done = visits.filter((v) => v.status === 'done' && v.date <= today).sort((a, b) => (a.date < b.date ? 1 : -1))
    const lastVisit = done[0] || null
    const msDay = 86400000
    const daysSince = lastVisit ? Math.max(0, Math.round((toDate(today).getTime() - toDate(lastVisit.date).getTime()) / msDay)) : null
    const missedThisMonth = visits.filter((v) => monthKey(v.date) === month && v.date < today && (v.status === 'missed' || v.status === 'planned')).length
    const state = storeState(goal, sold, month)
    const avg = orders.length ? orders.reduce((a, o) => a + o.total, 0) / orders.length : 0
    return { goal: goal, sold: sold, gap: goal ? Math.max(0, goal - sold) : 0, state: state, lastVisit: lastVisit, lastOrder: orders[0] || null, avgOrder: avg, daysSince: daysSince, missedThisMonth: missedThisMonth, orders: orders, visits: visits }
}

/**
 * The day of a salesperson split the way the app shows it: finished stops (in the order they happened), the one in
 * progress, the ones still to do (in route order) and the ones skipped / missed.
 */
export const splitDay = (agenda) => {
    const done = agenda.filter((x) => x.visit.status === 'done').sort((a, b) => (a.visit.checkInAt || 0) - (b.visit.checkInAt || 0))
    const current = agenda.filter((x) => x.visit.status === 'in_progress')[0] || null
    const pending = agenda.filter((x) => x.visit.status === 'planned')
    const missed = agenda.filter((x) => x.visit.status === 'missed')
    return { done: done, current: current, pending: pending, missed: missed }
}

/** Counters of a salesperson's day. */
export const repDayStats = (agenda) => {
    const sp = splitDay(agenda)
    const done = sp.done
    return {
        planned: agenda.length, done: done.length, left: sp.pending.length + (sp.current ? 1 : 0), missed: sp.missed.length,
        orders: done.filter((x) => x.visit.outcome === 'order').length, noOrder: done.filter((x) => x.visit.outcome === 'no_order').length, closed: done.filter((x) => x.visit.outcome === 'closed').length,
        minutes: done.reduce((a, x) => a + (x.visit.dur || 0), 0),
    }
}

/** Stores around a position that are not in today's plan, with the reason to stop by (see geo.rankNearby). */
export const nearbyCandidates = (sales, rep, excludeIds, vIdx, oIdx, unrouted) => {
    const today = todayIso()
    const out = []
    const skip = {}
    excludeIds.forEach((id) => { skip[id] = true })
    sales.stores.forEach((s) => {
        if (s.rep !== rep || skip[s.id]) return
        const info = storeInfo(sales, s.id, vIdx, oIdx)
        out.push({ store: s, gap: info.gap, state: info.state, daysSince: info.daysSince, missedThisMonth: info.missedThisMonth, unrouted: false })
    })
    ;(unrouted || []).forEach((u) => {
        if (u.rep !== rep) return
        const days = u.lastOrder ? Math.round((toDate(today).getTime() - toDate(u.lastOrder).getTime()) / 86400000) : null
        out.push({ store: u, gap: 0, state: null, daysSince: days, missedThisMonth: 0, unrouted: true })
    })
    return out
}
let unroutedCache = null
export const unroutedStores = () => { if (!unroutedCache) unroutedCache = buildUnrouted(); return unroutedCache }

/* ---------- chat ---------- */
export const threadOf = (field, actorKey) => field.threads[actorKey] || []
export const metaOf = (field, actorKey) => field.meta[actorKey] || { resolved: false, readByWorker: 0, readByManager: 0 }
/** Unread count from the point of view of one side ('worker' reads the manager's messages and the other way round). */
export const unreadFor = (field, actorKey, side) => {
    const m = metaOf(field, actorKey)
    const from = side === 'worker' ? 'manager' : 'worker'
    const read = side === 'worker' ? m.readByWorker : m.readByManager
    return threadOf(field, actorKey).filter((x) => (x.from === from || (from === 'manager' && x.from === 'system')) && x.at > read && !x.pending).length
}
export const totalManagerUnread = (field) => Object.keys(field.threads).reduce((a, k) => a + unreadFor(field, k, 'manager'), 0)

/** Things saved on the device while offline and not yet sent. */
export const pendingCount = (state) => {
    const f = state.field
    const msgs = Object.keys(f.threads).reduce((a, k) => a + f.threads[k].filter((m) => m.pending).length, 0)
    return msgs
        + state.sales.visits.filter((v) => v.pending).length + state.sales.orders.filter((o) => o.pending).length
        + state.drivers.deliveries.filter((d) => d.pending).length + f.returns.filter((r) => r.pending).length
}

export const openReturnsOf = (field, actorKey) => field.returns.filter((r) => r.actor === actorKey)
export const personName = (state, actorKey) => {
    if (actorKey.indexOf('rep-') === 0) return 'Salesperson ' + actorKey.slice(4)
    const d = state.drivers.drivers.filter((x) => 'drv-' + x.id === actorKey)[0]
    return d ? d.name : actorKey
}

/** How far a position is from a store / delivery, and the flag the reports use ("checked in far from the store"). */
export const FAR_MILES = 0.25
export const proximity = (pos, target) => {
    const d = pos && target ? miles(pos, target) : null
    return { distMi: d === null ? null : Math.round(d * 100) / 100, far: d !== null && d > FAR_MILES }
}

/* ======================================================================================================
 * REDUCER
 * ==================================================================================================== */

const patchList = (list, id, patch) => list.map((x) => (x.id === id ? Object.assign({}, x, typeof patch === 'function' ? patch(x) : patch) : x))

/** Appends a chat message with the next id. */
const addMessage = (field, actorKey, msg) => {
    const n = field.counters.m + 1
    const full = Object.assign({ id: 'm' + n, at: nowStamp(), photo: false, pending: false, ref: null }, msg)
    return Object.assign({}, field, { threads: Object.assign({}, field.threads, { [actorKey]: (field.threads[actorKey] || []).concat([full]) }), counters: Object.assign({}, field.counters, { m: n }) })
}

/** Back online: everything saved on the device while offline is "sent" (orders get their SO number now). */
const flushPending = (state) => {
    const f = state.field
    const ts = nowStamp()
    const soByOrder = {}
    const orders = state.sales.orders.map((o) => {
        if (!o.pending) return o
        const soNo = 'AS00' + (100 + Number(o.id.slice(1)))
        soByOrder[o.id] = soNo
        return Object.assign({}, o, { pending: false, soNo: soNo })
    })
    const visits = state.sales.visits.map((v) => {
        const soNo = v.soNo === '' ? '' : v.soNo
        const ord = orders.filter((o) => o.visitId === v.id && soByOrder[o.id])[0]
        return v.pending || ord ? Object.assign({}, v, { pending: false, soNo: ord ? ord.soNo : soNo }) : v
    })
    const deliveries = state.drivers.deliveries.map((d) => (d.pending ? Object.assign({}, d, { pending: false }) : d))
    const returns = f.returns.map((r) => (r.pending ? Object.assign({}, r, { pending: false }) : r))
    const threads = {}
    Object.keys(f.threads).forEach((k) => { threads[k] = f.threads[k].map((m) => (m.pending ? Object.assign({}, m, { pending: false, at: ts }) : m)) })
    const days = {}
    Object.keys(f.days).forEach((k) => { days[k] = f.days[k].pending ? Object.assign({}, f.days[k], { pending: false }) : f.days[k] })
    return Object.assign({}, state, { sales: Object.assign({}, state.sales, { orders: orders, visits: visits }), drivers: Object.assign({}, state.drivers, { deliveries: deliveries }), field: Object.assign({}, f, { online: true, returns: returns, threads: threads, days: days }) })
}

const baseReducer = (state, action) => {
    const sales = state.sales
    const drv = state.drivers
    switch (action.type) {
        case 'RESET':
            return initialState()

        /* ---------------------------------- goals ---------------------------------- */
        case 'GOAL_SET': {
            const g = Object.assign({}, sales.goals[action.month])
            const before = g[action.storeId]
            if (action.amount === null || action.amount === undefined) delete g[action.storeId]
            else g[action.storeId] = action.amount
            if (before === g[action.storeId]) return state
            const h = { id: 'h' + (sales.counters.h + 1), month: action.month, storeId: action.storeId, kind: 'goal', from: before === undefined ? null : before, to: g[action.storeId] === undefined ? null : g[action.storeId], by: action.by, at: nowStamp(), note: '' }
            return Object.assign({}, state, { sales: Object.assign({}, sales, { goals: Object.assign({}, sales.goals, { [action.month]: g }), goalHistory: [h].concat(sales.goalHistory), counters: Object.assign({}, sales.counters, { h: sales.counters.h + 1 }) }) })
        }
        case 'GOALS_BULK': {
            const g = Object.assign({}, sales.goals[action.month], action.entries)
            const h = { id: 'h' + (sales.counters.h + 1), month: action.month, storeId: null, kind: 'bulk', from: null, to: Object.keys(action.entries).length, by: action.by, at: nowStamp(), note: action.note }
            return Object.assign({}, state, { sales: Object.assign({}, sales, { goals: Object.assign({}, sales.goals, { [action.month]: g }), goalHistory: [h].concat(sales.goalHistory), counters: Object.assign({}, sales.counters, { h: sales.counters.h + 1 }) }) })
        }
        case 'PERIOD_STATUS': {
            const p = sales.periods[action.month]
            const next = Object.assign({}, p, { status: action.to })
            if (action.to === 'pending') { next.submittedBy = action.by; next.submittedAt = nowStamp() }
            if (action.to === 'published') { next.approvedBy = action.by; next.approvedAt = nowStamp(); next.published = Object.assign({}, sales.goals[action.month]) }
            const h = { id: 'h' + (sales.counters.h + 1), month: action.month, storeId: null, kind: 'status', from: p.status, to: action.to, by: action.by, at: nowStamp(), note: action.reason || '' }
            return Object.assign({}, state, { sales: Object.assign({}, sales, { periods: Object.assign({}, sales.periods, { [action.month]: next }), goalHistory: [h].concat(sales.goalHistory), counters: Object.assign({}, sales.counters, { h: sales.counters.h + 1 }) }) })
        }

        /* ---------------------------------- agenda ---------------------------------- */
        case 'AGENDA_SET': {
            const store = sales.stores.filter((s) => s.id === action.storeId)[0]
            const nextStore = Object.assign({}, store, { day: action.day, freq: action.freq })
            // seq: put it last in its new (rep, day) group
            const peers = sales.stores.filter((s) => s.id !== store.id && s.rep === store.rep && s.day === action.day)
            nextStore.seq = peers.reduce((m, s) => Math.max(m, s.seq), 0) + 1
            const today = todayIso()
            const keep = sales.visits.filter((v) => !(v.storeId === store.id && v.status === 'planned' && !v.extra && v.date > today))
            const cur = monthKey(today)
            let added = []
            let n = sales.counters.v
            plannedDates(nextStore, addDays(today, 1), cur + '-' + String(daysInMonth(cur)).padStart(2, '0')).forEach((d) => {
                n++
                added.push({ id: 'v' + n, storeId: store.id, rep: store.rep, date: d, status: 'planned', outcome: null, reason: '', notes: '', time: '', soNo: '', extra: false, ord: nextStore.seq })
            })
            return Object.assign({}, state, { sales: Object.assign({}, sales, { stores: patchList(sales.stores, store.id, nextStore), visits: keep.concat(added), counters: Object.assign({}, sales.counters, { v: n }) }) })
        }
        case 'AGENDA_MOVE': {
            const store = sales.stores.filter((s) => s.id === action.storeId)[0]
            const group = sales.stores.filter((s) => s.rep === store.rep && s.day === store.day).sort((a, b) => a.seq - b.seq)
            const i = group.findIndex((s) => s.id === store.id)
            const j = i + action.dir
            if (j < 0 || j >= group.length) return state
            const a = group[i]; const b = group[j]
            const todayKey = todayIso()
            const syncOrd = (v) => (v.status === 'planned' && !v.extra && v.date > todayKey && (v.storeId === a.id || v.storeId === b.id) ? Object.assign({}, v, { ord: v.storeId === a.id ? b.seq : a.seq }) : v)
            return Object.assign({}, state, { sales: Object.assign({}, sales, { stores: sales.stores.map((s) => (s.id === a.id ? Object.assign({}, s, { seq: b.seq }) : s.id === b.id ? Object.assign({}, s, { seq: a.seq }) : s)), visits: sales.visits.map(syncOrd) }) })
        }

        /* ---------------------------------- visits & orders ---------------------------------- */
        case 'VISIT_CHECKIN':
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, { status: 'done', outcome: action.outcome, reason: action.reason || '', notes: action.notes || '', time: hhmm(), soNo: action.soNo || '' }) }) })
        case 'VISIT_UNDO':
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, { status: 'planned', outcome: null, reason: '', notes: '', time: '', soNo: '' }) }) })
        case 'VISIT_EXTRA': {
            const n = sales.counters.v + 1
            const dayList = agendaFor(sales, action.rep, action.date).filter((x) => x.visit.status === 'planned')
            const firstOrd = dayList.length ? ordOf(dayList[0]) : 1
            const afterV = action.afterVisitId ? sales.visits.filter((x) => x.id === action.afterVisitId)[0] : null
            const ordNew = action.first ? firstOrd - 0.5 : afterV && afterV.status === 'planned' ? (afterV.ord || 0) + 0.5 : action.nextUp ? firstOrd - 0.5 : (dayList.length ? ordOf(dayList[dayList.length - 1]) : 0) + 1
            const v = { id: 'v' + n, storeId: action.storeId, rep: action.rep, date: action.date, status: 'planned', outcome: null, reason: '', notes: action.note || '', time: '', soNo: '', extra: true, ord: ordNew, source: action.source || 'manual', checkInAt: null, checkOutAt: null, dur: 0, distMi: null, far: false, missReason: '', photos: 0, retIds: [] }
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: sales.visits.concat([v]), counters: Object.assign({}, sales.counters, { v: n }) }) })
        }
        case 'VISIT_RESCHEDULE': {
            const orig = sales.visits.filter((v) => v.id === action.visitId)[0]
            const n = sales.counters.v + 1
            const moved = { id: 'v' + n, storeId: orig.storeId, rep: orig.rep, date: action.date, status: 'planned', outcome: null, reason: '', notes: action.reason ? 'Rescheduled: ' + action.reason : 'Rescheduled', time: '', soNo: '', extra: orig.extra, ord: orig.ord, checkInAt: null, checkOutAt: null, dur: 0, distMi: null, far: false, missReason: '', photos: 0, retIds: [] }
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, orig.id, { status: 'rescheduled', notes: action.reason || '' }).concat([moved]), counters: Object.assign({}, sales.counters, { v: n }) }) })
        }
        case 'ORDER_CREATE': {
            const n = sales.counters.o + 1
            const total = action.lines.reduce((a, l) => a + l.qty * l.price, 0)
            const soNo = action.pending ? '' : 'AS00' + (100 + n)
            const order = { id: 'o' + n, soNo: soNo, storeId: action.storeId, rep: action.rep, date: todayIso(), lines: action.lines, total: Math.round(total * 100) / 100, demo: true, visitId: action.visitId || null, notes: action.notes || '', deliverOn: action.deliverOn || '', pending: !!action.pending }
            let visits = sales.visits
            if (action.visitId) {
                const v = visits.filter((x) => x.id === action.visitId)[0]
                visits = patchList(visits, action.visitId, v && v.status === 'in_progress' ? { outcome: 'order', soNo: soNo } : { status: 'done', outcome: 'order', reason: '', time: hhmm(), soNo: soNo })
            }
            return Object.assign({}, state, { sales: Object.assign({}, sales, { orders: sales.orders.concat([order]), visits: visits, counters: Object.assign({}, sales.counters, { o: n }) }) })
        }
        case 'NOTE_ADD': {
            const n = sales.counters.n + 1
            const list = (sales.notes[action.storeId] || []).concat([{ id: 'n' + n, text: action.text, by: action.by, at: nowStamp() }])
            return Object.assign({}, state, { sales: Object.assign({}, sales, { notes: Object.assign({}, sales.notes, { [action.storeId]: list }), counters: Object.assign({}, sales.counters, { n: n }) }) })
        }

        /* ---------------------------------- deliveries ---------------------------------- */
        case 'DELIVERY_ADD': {
            let n = drv.counters.d
            const maxSeq = drv.deliveries.reduce((m, d) => Math.max(m, d.seq), 0)
            const added = action.items.map((r, i) => {
                n++
                const evs = [{ ts: nowStamp(), type: 'created', by: action.by, note: '' }]
                if (action.driverId) evs.push({ ts: nowStamp() + 1, type: 'assigned', by: action.by, note: 'Assigned to ' + drv.drivers.filter((d) => d.id === action.driverId)[0].name })
                return { id: 'dl' + n, invoiceNo: r.invoiceNo, customer: r.customer, address: r.address, city: r.city, phone: r.phone, window: action.window, weightLb: r.weightLb, notes: action.notes || '', driverId: action.driverId || null, date: action.date, originalDate: action.date, status: 'planned', deliveredOn: null, seq: maxSeq + i + 1, events: evs }
            })
            const taken = {}
            action.items.forEach((r) => { taken[r.invoiceNo] = true })
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: drv.deliveries.concat(added), ready: drv.ready.filter((r) => !taken[r.invoiceNo]), counters: Object.assign({}, drv.counters, { d: n }) }) })
        }
        case 'DELIVERY_ASSIGN': {
            const ids = {}
            action.ids.forEach((id) => { ids[id] = true })
            const name = action.driverId ? drv.drivers.filter((d) => d.id === action.driverId)[0].name : 'nobody'
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: drv.deliveries.map((d) => (ids[d.id] ? Object.assign({}, d, { driverId: action.driverId || null, stopOrder: null, events: d.events.concat([{ ts: nowStamp(), type: 'assigned', by: action.by, note: action.driverId ? 'Assigned to ' + name : 'Driver removed' }]) }) : d)) }) })
        }
        case 'DELIVERY_RESCHEDULE':
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: patchList(drv.deliveries, action.id, (d) => ({ date: action.date, stopOrder: null, events: d.events.concat([{ ts: nowStamp(), type: 'rescheduled', by: action.by, note: d.date + ' → ' + action.date + (action.reason ? ' · ' + action.reason : '') }]) })) }) })
        case 'DELIVERY_STATUS': {
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: patchList(drv.deliveries, action.id, (d) => {
                if (action.status === 'out') return { status: 'out', events: d.events.concat([{ ts: nowStamp(), type: 'out', by: action.by, note: '' }]) }
                if (action.status === 'delivered') return { status: 'delivered', deliveredOn: todayIso(), events: d.events.concat([{ ts: nowStamp(), type: 'delivered', by: action.by, note: action.note || '' }]) }
                if (action.status === 'failed') return { status: 'planned', date: action.newDate, stopOrder: null, events: d.events.concat([{ ts: nowStamp(), type: 'failed', by: action.by, note: action.reason + (action.note ? ' · ' + action.note : '') + ' → retry ' + action.newDate }]) }
                if (action.status === 'cancelled') return { status: 'cancelled', events: d.events.concat([{ ts: nowStamp(), type: 'cancelled', by: action.by, note: action.reason }]) }
                if (action.status === 'planned') return { status: 'planned', events: d.events.concat([{ ts: nowStamp(), type: 'back', by: action.by, note: 'Back to planned' }]) }
                return {}
            }) }) })
        }
        case 'DELIVERY_MOVE': {
            const list = orderedStops(drv, action.date, action.driverId)
            const i = list.findIndex((d) => d.id === action.id)
            const j = i + action.dir
            if (i < 0 || j < 0 || j >= list.length) return state
            const arr = list.slice()
            const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp
            const order = {}
            arr.forEach((d, k) => { order[d.id] = k + 1 })
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: drv.deliveries.map((d) => (order[d.id] ? Object.assign({}, d, { stopOrder: order[d.id] }) : d)) }) })
        }

        /* ---------------------------------- drivers & vehicles ---------------------------------- */
        case 'DRIVER_SAVE': {
            const exists = drv.drivers.some((d) => d.id === action.driver.id)
            if (exists) return Object.assign({}, state, { drivers: Object.assign({}, drv, { drivers: patchList(drv.drivers, action.driver.id, action.driver) }) })
            const n = drv.counters.drv + 1
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { drivers: drv.drivers.concat([Object.assign({}, action.driver, { id: 'd' + n, active: true })]), counters: Object.assign({}, drv.counters, { drv: n }) }) })
        }
        case 'DRIVER_ACTIVE':
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { drivers: patchList(drv.drivers, action.id, { active: action.active }) }) })
        case 'VEHICLE_SAVE': {
            const exists = drv.vehicles.some((v) => v.id === action.vehicle.id)
            if (exists) return Object.assign({}, state, { drivers: Object.assign({}, drv, { vehicles: patchList(drv.vehicles, action.vehicle.id, action.vehicle) }) })
            const n = drv.counters.veh + 1
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { vehicles: drv.vehicles.concat([Object.assign({}, action.vehicle, { id: 'v' + n, active: true })]), counters: Object.assign({}, drv.counters, { veh: n }) }) })
        }
        case 'VEHICLE_ACTIVE':
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { vehicles: patchList(drv.vehicles, action.id, { active: action.active }) }) })
        /* ---------------------------------- field app: network & demo switches ---------------------------------- */
        case 'NET_SET':
            return action.online ? flushPending(state) : Object.assign({}, state, { field: Object.assign({}, state.field, { online: false }) })
        case 'DEMO_SET':
            return Object.assign({}, state, { field: Object.assign({}, state.field, action.patch) })
        case 'POS_SET':
            return Object.assign({}, state, { field: Object.assign({}, state.field, { positions: Object.assign({}, state.field.positions, { [action.key]: { lat: action.lat, lng: action.lng, t: nowStamp() } }) }) })

        /* ---------------------------------- field app: day ---------------------------------- */
        case 'DAY_START': {
            const key = dayKey(action.actorKey, action.date)
            const prev = state.field.days[key] || {}
            const day = Object.assign({}, prev, { startedAt: prev.startedAt || nowStamp(), startOdo: action.odo === undefined ? prev.startOdo : action.odo, loadChecked: action.loadChecked, vehicleOk: action.vehicleOk, vehicleNote: action.vehicleNote || '', submitted: false, auto: false })
            return Object.assign({}, state, { field: Object.assign({}, state.field, { days: Object.assign({}, state.field.days, { [key]: day }) }) })
        }
        case 'DAY_END': {
            const key = dayKey(action.actorKey, action.date)
            const prev = state.field.days[key] || {}
            const ts = nowStamp()
            const day = Object.assign({}, prev, { startedAt: prev.startedAt || ts, endedAt: ts, endOdo: action.odo === undefined ? prev.endOdo : action.odo, miles: action.miles, note: action.note || '', submitted: true, submittedAt: ts, handover: action.handover || null, cash: action.cash || 0, pending: !!action.pending })
            let salesNext = sales
            let drvNext = drv
            let n = sales.counters.v
            const reasons = action.reasons || {}
            Object.keys(reasons).forEach((id) => {
                const r = reasons[id]
                const v = salesNext.visits.filter((x) => x.id === id)[0]
                if (v) {
                    let visits = patchList(salesNext.visits, id, { status: 'missed', missReason: r.reason, notes: r.note || v.notes })
                    if (r.moveTo) {
                        n++
                        visits = patchList(visits, id, { status: 'rescheduled', notes: r.reason }).concat([{ id: 'v' + n, storeId: v.storeId, rep: v.rep, date: r.moveTo, status: 'planned', outcome: null, reason: '', notes: 'Rescheduled: ' + r.reason, time: '', soNo: '', extra: v.extra, ord: v.ord, checkInAt: null, checkOutAt: null, dur: 0, distMi: null, far: false, missReason: '', photos: 0, retIds: [] }])
                    }
                    salesNext = Object.assign({}, salesNext, { visits: visits, counters: Object.assign({}, salesNext.counters, { v: n }) })
                }
                const dl = drvNext.deliveries.filter((x) => x.id === id)[0]
                if (dl && !r.moveTo) {
                    drvNext = Object.assign({}, drvNext, { deliveries: patchList(drvNext.deliveries, id, (d) => ({ events: d.events.concat([{ ts: ts, type: 'back', by: action.by, note: 'Not delivered today: ' + r.reason + (r.note ? ' · ' + r.note : '') }]) })) })
                }
                if (dl && r.moveTo) {
                    drvNext = Object.assign({}, drvNext, { deliveries: patchList(drvNext.deliveries, id, (d) => ({ date: r.moveTo, status: 'planned', stopOrder: null, arrivedAt: null, events: d.events.concat([{ ts: ts, type: 'rescheduled', by: action.by, note: d.date + ' → ' + r.moveTo + ' · ' + r.reason }]) })) })
                }
            })
            let field = Object.assign({}, state.field, { days: Object.assign({}, state.field.days, { [key]: day }) })
            field = addMessage(field, action.actorKey, { from: 'system', by: 'System', text: (action.by || 'Worker') + ' submitted the day report (' + action.summary + ').', topic: 'general', ref: { type: 'day', id: key, label: 'Day report' } })
            return Object.assign({}, state, { sales: salesNext, drivers: drvNext, field: field })
        }

        /* ---------------------------------- field app: salesperson visit ---------------------------------- */
        case 'VISIT_CHECKIN2': {
            const ts = nowStamp()
            const key = dayKey(action.actorKey, action.date)
            const prev = state.field.days[key]
            const days = prev && prev.startedAt ? state.field.days : Object.assign({}, state.field.days, { [key]: Object.assign({}, prev || {}, { startedAt: ts, submitted: false, auto: true }) })
            return Object.assign({}, state, {
                sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, { status: 'in_progress', checkInAt: ts, lat: action.lat, lng: action.lng, distMi: action.distMi, far: !!action.far, farReason: action.farReason || '', manual: !!action.manual, pending: !!action.pending }) }),
                field: Object.assign({}, state.field, { days: days, positions: Object.assign({}, state.field.positions, { [action.actorKey]: { lat: action.lat, lng: action.lng, t: ts } }) }),
            })
        }
        case 'VISIT_CANCEL_CHECKIN':
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, { status: 'planned', checkInAt: null, outcome: null, soNo: '', far: false, farReason: '', distMi: null, pending: false }) }) })
        case 'VISIT_COMPLETE': {
            const ts = nowStamp()
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, (v) => ({ status: 'done', outcome: action.outcome, reason: action.reason || '', notes: action.notes === undefined ? v.notes : action.notes, checkOutAt: ts, dur: Math.max(1, Math.round((ts - (v.checkInAt || ts)) / 60000)), time: hhmm(), followUp: action.followUp || '' })) }) })
        }
        case 'VISIT_SKIP':
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, { status: 'missed', missReason: action.reason, skipped: true, notes: action.note || '' }) }) })
        case 'VISIT_MOVE': {
            const list = agendaFor(sales, action.rep, action.date).filter((x) => x.visit.status === 'planned')
            const i = list.findIndex((x) => x.visit.id === action.visitId)
            const j = i + action.dir
            if (i < 0 || j < 0 || j >= list.length) return state
            const ids = list.map((x) => x.visit.id)
            const t = ids[i]; ids[i] = ids[j]; ids[j] = t
            const ord = {}
            ids.forEach((id, k) => { ord[id] = k + 1 })
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: sales.visits.map((v) => (ord[v.id] ? Object.assign({}, v, { ord: ord[v.id] }) : v)) }) })
        }
        case 'VISIT_REORDER': {
            const ord = {}
            action.ids.forEach((id, k) => { ord[id] = k + 1 })
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: sales.visits.map((v) => (ord[v.id] ? Object.assign({}, v, { ord: ord[v.id] }) : v)) }) })
        }
        case 'VISIT_REMOVE':
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: sales.visits.filter((v) => v.id !== action.visitId) }) })
        case 'VISIT_PHOTO':
            return Object.assign({}, state, { sales: Object.assign({}, sales, { visits: patchList(sales.visits, action.visitId, (v) => ({ photos: (v.photos || 0) + 1 })) }) })

        /* ---------------------------------- field app: driver stop ---------------------------------- */
        case 'DELIVERY_ARRIVE': {
            const ts = nowStamp()
            const key = dayKey(action.actorKey, action.date)
            const prev = state.field.days[key]
            const days = prev && prev.startedAt ? state.field.days : Object.assign({}, state.field.days, { [key]: Object.assign({}, prev || {}, { startedAt: ts, submitted: false, auto: true }) })
            return Object.assign({}, state, {
                drivers: Object.assign({}, drv, { deliveries: patchList(drv.deliveries, action.id, (d) => ({ status: 'out', arrivedAt: ts, lat: d.lat, lng: d.lng, distMi: action.distMi, far: !!action.far, farReason: action.farReason || '', pending: !!action.pending, events: d.events.concat([{ ts: ts, type: d.status === 'out' ? 'arrived' : 'out', by: action.by, note: d.status === 'out' ? 'Arrived at the stop' : 'Started and arrived' }]) })) }),
                field: Object.assign({}, state.field, { days: days, positions: Object.assign({}, state.field.positions, { [action.actorKey]: { lat: action.lat, lng: action.lng, t: ts } }) }),
            })
        }
        case 'DELIVERY_UNARRIVE':
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: patchList(drv.deliveries, action.id, { arrivedAt: null, far: false, farReason: '', distMi: null }) }) })
        case 'DELIVERY_COMPLETE': {
            const ts = nowStamp()
            const partial = (action.shortLines || []).length > 0
            return Object.assign({}, state, { drivers: Object.assign({}, drv, { deliveries: patchList(drv.deliveries, action.id, (d) => ({
                status: 'delivered', deliveredOn: todayIso(), doneAt: ts, receivedBy: action.receivedBy || '', signed: !!action.signed, payment: action.payment || null, shortLines: action.shortLines || [], photos: action.photos || 0, pending: !!action.pending,
                events: d.events.concat([{ ts: ts, type: 'delivered', by: action.by, note: (partial ? 'Partial delivery. ' : '') + (action.note || '') }]),
            })) }) })
        }

        /* ---------------------------------- field app: returns ---------------------------------- */
        case 'RETURN_CREATE': {
            const f = state.field
            const n = f.counters.r + 1
            const ts = nowStamp()
            const total = Math.round(action.ret.lines.reduce((a, l) => a + l.qty * l.price, 0) * 100) / 100
            const ret = Object.assign({}, action.ret, { id: 'r' + n, no: 'RT-' + (100 + n), at: ts, date: todayIso(), total: total, status: 'requested', pending: !!action.pending, history: [{ ts: ts, type: 'requested', by: action.ret.by, note: '' }] })
            let field = Object.assign({}, f, { returns: [ret].concat(f.returns), counters: Object.assign({}, f.counters, { r: n }) })
            field = addMessage(field, ret.actor, { from: 'system', by: 'System', text: 'Return ' + ret.no + ' requested by ' + ret.by + ' — $' + total.toFixed(2) + ' (' + ret.lines.length + ' item' + (ret.lines.length === 1 ? '' : 's') + ').', topic: 'return', ref: { type: 'return', id: ret.id, label: ret.no } })
            let salesNext = sales
            let drvNext = drv
            if (ret.source === 'visit') salesNext = Object.assign({}, sales, { visits: patchList(sales.visits, ret.refId, (v) => ({ retIds: (v.retIds || []).concat([ret.id]) })) })
            if (ret.source === 'delivery') drvNext = Object.assign({}, drv, { deliveries: patchList(drv.deliveries, ret.refId, (d) => ({ retIds: (d.retIds || []).concat([ret.id]) })) })
            return Object.assign({}, state, { sales: salesNext, drivers: drvNext, field: field })
        }
        case 'RETURN_STATUS': {
            const f = state.field
            const ts = nowStamp()
            const target = f.returns.filter((r) => r.id === action.id)[0]
            if (!target) return state
            let field = Object.assign({}, f, { returns: f.returns.map((r) => (r.id === action.id ? Object.assign({}, r, { status: action.status, history: r.history.concat([{ ts: ts, type: action.status, by: action.by, note: action.note || '' }]) }) : r)) })
            field = addMessage(field, target.actor, { from: 'manager', by: action.by, text: 'Return ' + target.no + ' was ' + action.status.replace('_', ' ') + '.' + (action.note ? ' ' + action.note : ''), topic: 'return', ref: { type: 'return', id: target.id, label: target.no } })
            return Object.assign({}, state, { field: field })
        }

        /* ---------------------------------- field app: chat ---------------------------------- */
        case 'CHAT_SEND': {
            let field = addMessage(state.field, action.actorKey, { from: action.from, by: action.by, text: action.text, topic: action.topic || 'general', ref: action.ref || null, photo: !!action.photo, pending: !!action.pending })
            const m = metaOf(field, action.actorKey)
            const mine = action.from === 'worker' ? { readByWorker: nowStamp(), resolved: false } : { readByManager: nowStamp() }
            field = Object.assign({}, field, { meta: Object.assign({}, field.meta, { [action.actorKey]: Object.assign({}, m, mine) }) })
            return Object.assign({}, state, { field: field })
        }
        case 'CHAT_READ': {
            const m = metaOf(state.field, action.actorKey)
            const patch = action.side === 'worker' ? { readByWorker: nowStamp() } : { readByManager: nowStamp() }
            return Object.assign({}, state, { field: Object.assign({}, state.field, { meta: Object.assign({}, state.field.meta, { [action.actorKey]: Object.assign({}, m, patch) }) }) })
        }
        case 'CHAT_RESOLVE': {
            const m = metaOf(state.field, action.actorKey)
            return Object.assign({}, state, { field: Object.assign({}, state.field, { meta: Object.assign({}, state.field.meta, { [action.actorKey]: Object.assign({}, m, { resolved: !!action.resolved }) }) }) })
        }

        default:
            return state
    }
}

export const reducer = (state, action) => {
    clock = action && action.ts ? action.ts : null
    try { return baseReducer(state, action) } finally { clock = null }
}
